// Global planner-draft persistence layer (Phase B2 + B3B).
//
// Responsibilities:
//   1. Anonymous save via upsert_anonymous_draft while unauthenticated.
//   2. On phone-OTP sign-in, one-shot complete_customer_onboarding
//      (idempotent by client onboarding_request_id) using values
//      already collected by the wizard, followed by
//      claim_planner_draft(anonymous_draft_id, 'reject_if_owned').
//   3. Authenticated save via upsert_authenticated_planner_draft with
//      optimistic-concurrency (p_expected_revision).
//   4. Resume behavior across refresh (anon UUID persisted; auth
//      revision persisted in sessionStorage under a stable key).
//   5. Sign-out cleanup: forget authenticated bookkeeping only; do
//      NOT delete customers, drafts, or push authenticated data back
//      into the anonymous slot.
//
// This component is rendered once, near the router root, and produces
// no UI.  All customer-facing UI stays in the existing wizard.

import { useEffect, useRef, useState } from 'react';
import { supabase, supabaseConfigured } from './supabase';
import { useDraft } from './plannerDraft';
import { useSession } from './useSession';
import {
  getOrCreateAnonymousDraftId,
  peekAnonymousDraftId,
  clearAnonymousDraftId,
} from './anonymousDraftId';
import {
  toServerDraft,
  hasAnyWizardInput,
  draftSignature,
  isOnboardingEligible,
  buildOnboardingArgs,
} from './draftMapping';

const DEBOUNCE_MS = 800;
const SAVE_RETRY_MS = 2500;
const MAX_SAVE_ATTEMPTS = 3; // bounded stale_revision reconciliation attempts
const ONBOARDING_REQ_KEY = 'etff.onboarding_request_id.v1';
const AUTH_STATE_KEY = 'etff.authenticated_draft.v1'; // { customer_id, revision }

// Module-level flush registry.
//   flushDraftNow() forces the debounced save to run NOW and awaits
//   its outcome, so callers (e.g. Step10Generating) can guarantee
//   the freshest planner inputs are persisted server-side before
//   invoking generate_meal_plans. Returns:
//     { ok: true,  outcome: 'saved' | 'noop' | 'nothing_to_save' }
//     { ok: false, outcome: 'stale_revision' | 'existing_auth_draft'
//                            | 'onboarding_pending' | 'no_sync_mounted'
//                            | 'save_failed' | 'not_configured' }
let flushImpl = null;
export function flushDraftNow() {
  if (typeof flushImpl !== 'function') {
    return Promise.resolve({ ok: false, outcome: 'no_sync_mounted' });
  }
  return flushImpl();
}

function isBrowser() {
  return typeof window !== 'undefined' && !!window.sessionStorage;
}

function makeUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  buf[6] = (buf[6] & 0x0f) | 0x40;
  buf[8] = (buf[8] & 0x3f) | 0x80;
  const h = [...buf].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function getOrCreateOnboardingRequestId() {
  if (!isBrowser()) return makeUuid();
  try {
    let id = window.sessionStorage.getItem(ONBOARDING_REQ_KEY);
    if (id && /^[0-9a-f-]{36}$/i.test(id)) return id;
    id = makeUuid();
    window.sessionStorage.setItem(ONBOARDING_REQ_KEY, id);
    return id;
  } catch {
    return makeUuid();
  }
}

function clearOnboardingRequestId() {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(ONBOARDING_REQ_KEY);
  } catch {
    /* ignore */
  }
}

function readAuthState() {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(AUTH_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && Number.isFinite(parsed.revision)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeAuthState(state) {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(AUTH_STATE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota */
  }
}

function clearAuthState() {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(AUTH_STATE_KEY);
  } catch {
    /* ignore */
  }
}

function logSanitized(label, err) {
  try {
    console.warn('[etff:draft]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
    });
  } catch {
    /* ignore */
  }
}

function logInfo(label, payload) {
  try {
    console.info('[etff:draft]', label, payload || {});
  } catch {
    /* ignore */
  }
}

// True for transient conditions the client should retry.
function isTransientError(err) {
  if (!err) return false;
  const code = err.code || '';
  const msg = (err.message || '').toLowerCase();
  if (code === '55000') return true; // throttle
  if (msg.includes('fetch') || msg.includes('network')) return true;
  if (msg.includes('timeout')) return true;
  return false;
}

/**
 * PlannerDraftSync — the mounted component. No DOM.
 */
export function PlannerDraftSync() {
  const { draft } = useDraft();
  const { session } = useSession();
  const [ready, setReady] = useState(false);

  const debounceTimer = useRef(null);
  const retryTimer = useRef(null);
  const inFlight = useRef(false);
  const lastSavedSig = useRef(null);
  const revisionRef = useRef(readAuthState()?.revision ?? null);
  const onboardingRunning = useRef(false);
  const onboardingCompletedForUid = useRef(readAuthState()?.customer_id ?? null);
  const conflictRef = useRef(null); // 'stale_revision' | 'existing_auth_draft' | null
  const prevUidRef = useRef(null);

  // Bootstrap: ensure an anon UUID exists in localStorage on first mount.
  useEffect(() => {
    getOrCreateAnonymousDraftId();
    setReady(true);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (retryTimer.current) clearTimeout(retryTimer.current);
      flushImpl = null;
    };
  }, []);

  // Track sign-in / sign-out transitions.
  useEffect(() => {
    const uid = session?.user?.id || null;
    const prev = prevUidRef.current;
    prevUidRef.current = uid;

    // signed-out transition
    if (prev && !uid) {
      // Preserve server-side authenticated draft.  Only clear local
      // bookkeeping so we don't accidentally send stale expected_revision
      // requests as a new anonymous session.
      revisionRef.current = null;
      onboardingCompletedForUid.current = null;
      conflictRef.current = null;
      clearAuthState();
      clearOnboardingRequestId();
      logInfo('signed_out:cleanup');
    }

    // signed-in transition (or refresh with pre-existing session)
    if (uid && prev !== uid) {
      // If a persisted revision exists for this same uid, resume with it.
      const persisted = readAuthState();
      if (persisted && persisted.customer_id === uid) {
        revisionRef.current = persisted.revision;
        onboardingCompletedForUid.current = uid;
        logInfo('signed_in:resume', { revision: persisted.revision });
      } else {
        revisionRef.current = null;
        onboardingCompletedForUid.current = null;
        clearAuthState();
        logInfo('signed_in:fresh');
      }
    }
  }, [session?.user?.id]);

  // Debounced save + onboarding+claim on every draft or session change.
  useEffect(() => {
    if (!ready) return;
    if (!supabaseConfigured || !supabase) {
      flushImpl = () => Promise.resolve({ ok: false, outcome: 'not_configured' });
      return;
    }

    // Register the flush hook so external callers (Step10Generating)
    // can force an immediate save and await its outcome.
    flushImpl = async () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      // Wait for any in-flight save to finish before starting.
      while (inFlight.current) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 25));
      }
      // A prior conflict may have blocked auto-saves. The customer's
      // explicit Build-my-plans click is a signal to attempt recovery
      // once more; clear the flag and rely on saveAuthenticated's
      // built-in reconciliation to resolve the revision. If it still
      // fails, saveAuthenticated will re-set conflictRef and we return
      // stale_revision to the caller.
      conflictRef.current = null;
      if (!hasAnyWizardInput(draft)) {
        return { ok: true, outcome: 'nothing_to_save' };
      }
      // Force a genuine save even if the signature matches (e.g. the
      // customer navigated back and clicked Build my plans without
      // changing anything — we still want to be sure the server has
      // the latest values).
      lastSavedSig.current = null;
      return runOnce();
    };

    if (!hasAnyWizardInput(draft)) return;

    // If we hit a conflict that requires user action, do not auto-save.
    if (conflictRef.current) return;

    // Schedule a debounced save.
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(runOnce, DEBOUNCE_MS);

    async function runOnce() {
      if (inFlight.current) return { ok: false, outcome: 'busy' };
      inFlight.current = true;
      try {
        const uid = session?.user?.id || null;
        if (!uid) {
          return await saveAnonymous();
        }
        const needsOnboarding = onboardingCompletedForUid.current !== uid;
        if (needsOnboarding) {
          const done = await runOnboardingAndClaim(uid);
          if (!done) return { ok: false, outcome: 'onboarding_pending' };
        }
        return await saveAuthenticated(uid);
      } finally {
        inFlight.current = false;
      }
    }

    async function saveAnonymous() {
      const anonId = getOrCreateAnonymousDraftId();
      if (!anonId) return { ok: false, outcome: 'no_anon_id' };
      const server = toServerDraft(draft);
      const sig = 'anon:' + anonId + ':' + draftSignature(server);
      if (sig === lastSavedSig.current) return { ok: true, outcome: 'noop' };
      try {
        const { data, error } = await supabase.rpc('upsert_anonymous_draft', {
          p_anonymous_draft_id: anonId,
          p_draft: server,
        });
        if (error) {
          logSanitized('upsert_anonymous_draft:error', error);
          scheduleRetryIfTransient(error);
          return { ok: false, outcome: 'save_failed' };
        }
        lastSavedSig.current = sig;
        logInfo('upsert_anonymous_draft:ok', {
          bytes: data?.bytes || null,
          expires_at: data?.expires_at || null,
        });
        return { ok: true, outcome: 'saved' };
      } catch (err) {
        logSanitized('upsert_anonymous_draft:threw', err);
        scheduleRetryIfTransient(err);
        return { ok: false, outcome: 'save_failed' };
      }
    }

    async function saveAuthenticated(uid, attempt = 1) {
      const server = toServerDraft(draft);
      const sig = 'auth:' + uid + ':' + draftSignature(server);
      if (sig === lastSavedSig.current) return { ok: true, outcome: 'noop' };
      const expected =
        revisionRef.current === null || revisionRef.current === undefined
          ? 0
          : revisionRef.current;
      try {
        const { data, error } = await supabase.rpc('upsert_authenticated_planner_draft', {
          p_draft: server,
          p_expected_revision: expected,
        });
        if (error) {
          logSanitized('upsert_authenticated_planner_draft:error', error);
          scheduleRetryIfTransient(error);
          return { ok: false, outcome: 'save_failed' };
        }
        const outcome = data?.outcome;
        if (outcome === 'saved') {
          const rev = Number.isFinite(data?.revision) ? data.revision : null;
          if (rev !== null) {
            revisionRef.current = rev;
            writeAuthState({ customer_id: uid, revision: rev });
          }
          lastSavedSig.current = sig;
          conflictRef.current = null; // any prior conflict is resolved
          logInfo('upsert_authenticated_planner_draft:ok', { revision: rev, attempt });
          return { ok: true, outcome: 'saved' };
        }
        if (outcome === 'stale_revision') {
          const serverRev = Number.isFinite(data?.current_revision)
            ? data.current_revision
            : null;
          const canRetry = attempt < MAX_SAVE_ATTEMPTS && serverRev !== null;
          logInfo('upsert_authenticated_planner_draft:stale_revision', {
            client_revision: expected,
            server_revision: serverRev,
            attempt,
            will_recover: canRetry,
          });
          // Reconcile using the authoritative server revision and retry
          // the customer's latest local values, up to MAX_SAVE_ATTEMPTS
          // total attempts. Stops immediately once the save succeeds;
          // stops and surfaces a customer-safe conflict once the bounded
          // retry budget is exhausted.
          if (canRetry) {
            revisionRef.current = serverRev;
            writeAuthState({ customer_id: uid, revision: serverRev });
            lastSavedSig.current = null; // force the retry to actually send
            return await saveAuthenticated(uid, attempt + 1);
          }
          conflictRef.current = 'stale_revision';
          return { ok: false, outcome: 'stale_revision' };
        }
        if (outcome === 'customer_not_initialized') {
          // Missed onboarding — try to run it once more on next tick.
          onboardingCompletedForUid.current = null;
          logInfo('upsert_authenticated_planner_draft:customer_not_initialized');
          return { ok: false, outcome: 'onboarding_pending' };
        }
        logInfo('upsert_authenticated_planner_draft:unexpected', {
          outcome: outcome || null,
        });
        return { ok: false, outcome: 'save_failed' };
      } catch (err) {
        logSanitized('upsert_authenticated_planner_draft:threw', err);
        scheduleRetryIfTransient(err);
        return { ok: false, outcome: 'save_failed' };
      }
    }

    async function runOnboardingAndClaim(uid) {
      if (onboardingRunning.current) return false;
      if (!isOnboardingEligible(draft)) {
        logInfo('onboarding:skipped_ineligible');
        return false;
      }
      const built = buildOnboardingArgs(draft);
      if (!built.ok) {
        logInfo('onboarding:skipped_missing_fields', { missing: built.missing });
        return false;
      }
      onboardingRunning.current = true;
      try {
        const reqId = getOrCreateOnboardingRequestId();
        const args = { ...built.args, p_onboarding_request_id: reqId };
        const { data, error } = await supabase.rpc('complete_customer_onboarding', args);
        if (error) {
          logSanitized('complete_customer_onboarding:error', error);
          scheduleRetryIfTransient(error);
          return false;
        }
        logInfo('complete_customer_onboarding:ok', {
          onboarded_at: data?.onboarded_at || null,
          onboarding_request_id: data?.onboarding_request_id || null,
          identity_synced_from_auth: data?.identity_synced_from_auth || null,
        });

        // Claim the anonymous draft (if one exists in this browser).
        const anonId = peekAnonymousDraftId();
        if (anonId) {
          const { data: cdata, error: cerror } = await supabase.rpc(
            'claim_planner_draft',
            {
              p_anonymous_draft_id: anonId,
              p_conflict_strategy: 'reject_if_owned',
            }
          );
          if (cerror) {
            logSanitized('claim_planner_draft:error', cerror);
            scheduleRetryIfTransient(cerror);
            return false;
          }
          const outcome = cdata?.outcome;
          if (outcome === 'claimed_ok' || outcome === 'already_claimed_self') {
            const rev = Number.isFinite(cdata?.revision) ? cdata.revision : 0;
            revisionRef.current = rev;
            writeAuthState({ customer_id: uid, revision: rev });
            clearAnonymousDraftId(); // future anonymous work uses a fresh id
            logInfo('claim_planner_draft:ok', { outcome, revision: rev });
          } else if (outcome === 'conflict_existing_authenticated_draft') {
            const serverRev = Number.isFinite(cdata?.current_revision)
              ? cdata.current_revision
              : null;
            conflictRef.current = 'existing_auth_draft';
            revisionRef.current = serverRev;
            if (serverRev !== null) writeAuthState({ customer_id: uid, revision: serverRev });
            logInfo('claim_planner_draft:conflict_existing_authenticated_draft', {
              server_revision: serverRev,
            });
            // Preserve the authenticated draft; consumer UI (later
            // phase) will surface a safe resume choice.  Do not
            // overwrite.
          } else if (outcome === 'invalid_input' || outcome === 'unavailable') {
            logInfo('claim_planner_draft:' + outcome);
          } else {
            logInfo('claim_planner_draft:unexpected', { outcome: outcome || null });
          }
        } else {
          // Onboarded without an anonymous draft in this browser: start
          // authenticated saves at revision 0.
          revisionRef.current = 0;
          writeAuthState({ customer_id: uid, revision: 0 });
          logInfo('claim_planner_draft:no_anonymous_draft_present');
        }

        onboardingCompletedForUid.current = uid;
        return true;
      } catch (err) {
        logSanitized('onboarding_or_claim:threw', err);
        scheduleRetryIfTransient(err);
        return false;
      } finally {
        onboardingRunning.current = false;
      }
    }

    function scheduleRetryIfTransient(err) {
      if (!isTransientError(err)) return;
      if (retryTimer.current) clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(() => {
        // Nudge a re-run by invalidating the last saved signature.
        lastSavedSig.current = null;
        runOnce();
      }, SAVE_RETRY_MS);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, [ready, draft, session?.user?.id]);

  return null;
}

export default PlannerDraftSync;
